import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Send, Linkedin, MessageCircle, Clock, Users, BookOpen, ArrowRight, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse response:', jsonError);
        // Check if it's a validation error based on status
        if (response.status === 422) {
          setError('Please check your form: Message must be at least 10 characters long.');
        } else {
          setError('Server error. Please try again later.');
        }
        return;
      }

      if (response.ok && data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        // Reset form after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      } else if (response.status === 422) {
        // Validation error
        if (data.detail && Array.isArray(data.detail)) {
          const errorMessages = data.detail.map(err => {
            const field = err.loc ? err.loc[err.loc.length - 1] : '';
            return `${field}: ${err.msg}`;
          }).join('. ');
          setError(errorMessages || 'Please check your form inputs and try again.');
        } else {
          setError('Please fill in all fields correctly. Message must be at least 10 characters.');
        }
      } else {
        setError(data.detail || data.message || 'Failed to submit form. Please try again.');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Unable to connect to server. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="Contact Vitta Finance Academy - CIRO Exam Prep Inquiries & Support"
        description="Contact Vitta Finance Academy for CIRO exam prep inquiries. Email: info@vittafinance.ca. Connect with Dr. Amina Beecroft on LinkedIn. Get help with CIRE, RSE, ISE, Derivatives course enrollment, bulk orders, and institutional packages. Located in Alberta, serving all of Canada."
        keywords="contact Vitta Finance, CIRO exam help, CIRO exam inquiries, course enrollment CIRO, bulk orders CIRO exam, institutional training CIRO, exam prep support Canada, CIRO tutor contact, CIRO exam questions, info@vittafinance.ca, Dr. Amina Beecroft contact, CIRO exam support Alberta, CIRO exam support Ontario, CIRO exam support Toronto"
        ogTitle="Contact Vitta Finance Academy - CIRO Exam Prep Help & Inquiries"
        ogDescription="Reach out for CIRO exam prep inquiries, course enrollment, bulk orders, and institutional packages. Connect with our expert instructors."
      />

      {/* Hero Section */}
      <section style={{
        padding: '100px 0 80px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5
        }}></div>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: 800,
              marginBottom: '24px',
              lineHeight: 1.2
            }}>
              Let's Start a <span style={{ color: 'var(--gold-primary)' }}>Conversation</span>
            </h1>
            <p style={{
              fontSize: '1.25rem',
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Have questions about CIRO exam prep? We're here to help you achieve certification success.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section style={{
        padding: '0',
        marginTop: '-50px',
        position: 'relative',
        zIndex: 2
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {/* Email Card */}
            <a href="mailto:info@vittafinance.ca" style={{
              padding: '32px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }} className="card-hover-effect">
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: 'rgba(30, 58, 138, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Mail size={28} color="var(--brand-primary)" />
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Email Us</p>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--brand-primary)' }}>info@vittafinance.ca</p>
              </div>
            </a>

            {/* LinkedIn Card */}
            <a href="https://www.linkedin.com/in/amina-beecroft/" target="_blank" rel="noopener noreferrer" style={{
              padding: '32px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
            }} className="card-hover-effect">
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: 'rgba(0, 119, 181, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Linkedin size={28} color="#0077b5" />
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Connect on LinkedIn</p>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0077b5' }}>Dr. Amina Beecroft</p>
              </div>
            </a>

            {/* Location Card */}
            <div style={{
              padding: '32px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '20px'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                backgroundColor: 'rgba(212, 175, 55, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <MapPin size={28} color="var(--gold-primary)" />
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '4px' }}>Located In</p>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)' }}>Alberta, Canada</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Left Column - Info */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                How Can We Help?
              </h2>
              <p className="body-medium" style={{ 
                marginBottom: '40px', 
                color: 'var(--text-secondary)',
                lineHeight: 1.8
              }}>
                Whether you're an individual learner, educational institution, or financial services firm, we're here to support your CIRO certification journey.
              </p>

              {/* Help Topics */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '40px' }}>
                {[
                  { icon: <BookOpen size={22} />, title: 'Course Inquiries', desc: 'Questions about CIRE, RSE, ISE, or Derivatives courses' },
                  { icon: <Users size={22} />, title: 'Institutional Packages', desc: 'Bulk orders and corporate training solutions' },
                  { icon: <MessageCircle size={22} />, title: 'Technical Support', desc: 'Help with platform access or course materials' },
                  { icon: <Clock size={22} />, title: 'General Questions', desc: 'Anything else we can help you with' }
                ].map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    gap: '16px',
                    padding: '20px',
                    backgroundColor: 'var(--bg-card)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-light)'
                  }}>
                    <div style={{ color: 'var(--brand-primary)', flexShrink: 0 }}>{item.icon}</div>
                    <div>
                      <h4 style={{ fontWeight: 600, marginBottom: '4px', fontSize: '1rem' }}>{item.title}</h4>
                      <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Links */}
              <div style={{
                padding: '32px',
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, #3b82f6 100%)',
                borderRadius: '16px',
                color: 'white'
              }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '20px' }}>Quick Links</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <a href="https://www.vittafinance.ca/store" target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 20px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'background-color 0.3s ease'
                  }}>
                    Browse All Courses <ArrowRight size={18} />
                  </a>
                  <Link to="/institutional" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 20px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'background-color 0.3s ease'
                  }}>
                    Institutional Packages <ArrowRight size={18} />
                  </Link>
                  <a href="https://vittafinance.mykajabi.com/login" target="_blank" rel="noopener noreferrer" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 20px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: 'white',
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'background-color 0.3s ease'
                  }}>
                    Member Login <ArrowRight size={18} />
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '48px',
              borderRadius: '20px',
              border: '1px solid var(--border-light)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}>
              <h2 className="heading-3" style={{ marginBottom: '8px' }}>
                Send Us a Message
              </h2>
              <p style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
                We typically respond within 24 hours
              </p>
              
              {submitted ? (
                <div style={{
                  padding: '48px 32px',
                  background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                  borderRadius: '16px',
                  textAlign: 'center',
                  border: '1px solid var(--brand-primary)'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    backgroundColor: 'var(--brand-primary)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                  }}>
                    <Send size={28} color="white" />
                  </div>
                  <h3 className="heading-4" style={{ marginBottom: '12px', color: 'var(--brand-primary)' }}>
                    Message Sent!
                  </h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    Thank you for reaching out. We'll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label htmlFor="name" style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        fontSize: '0.95rem'
                      }}>
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          backgroundColor: 'var(--bg-page)',
                          border: '2px solid var(--border-light)',
                          borderRadius: '10px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          fontFamily: 'inherit',
                          transition: 'border-color 0.3s ease'
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        fontSize: '0.95rem'
                      }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        style={{
                          width: '100%',
                          padding: '14px 18px',
                          backgroundColor: 'var(--bg-page)',
                          border: '2px solid var(--border-light)',
                          borderRadius: '10px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          fontFamily: 'inherit',
                          transition: 'border-color 0.3s ease'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="subject" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      fontSize: '0.95rem'
                    }}>
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        backgroundColor: 'var(--bg-page)',
                        border: '2px solid var(--border-light)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.3s ease',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Select a topic...</option>
                      <option value="course">Course Inquiry</option>
                      <option value="institutional">Institutional Package</option>
                      <option value="bulk">Bulk Order</option>
                      <option value="technical">Technical Support</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '28px' }}>
                    <label htmlFor="message" style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      fontSize: '0.95rem'
                    }}>
                      Your Message * <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(min. 10 characters)</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={10}
                      rows="5"
                      placeholder="How can we help you? (Please write at least 10 characters)"
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        backgroundColor: 'var(--bg-page)',
                        border: '2px solid var(--border-light)',
                        borderRadius: '10px',
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'border-color 0.3s ease'
                      }}
                    />
                  </div>

                  {error && (
                    <div style={{
                      padding: '16px',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid #ef4444',
                      borderRadius: '10px',
                      marginBottom: '20px',
                      color: '#dc2626',
                      fontSize: '0.95rem'
                    }}>
                      {error}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="btn-gold" 
                    disabled={loading}
                    style={{ 
                      width: '100%', 
                      padding: '16px 32px',
                      fontSize: '1.1rem',
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? (
                      <>
                        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} /> Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bulk & Institutional Orders Section */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ 
              fontSize: 'clamp(2rem, 4vw, 2.75rem)', 
              fontWeight: 800, 
              marginBottom: '40px',
              color: 'white'
            }}>
              Bulk & Institutional Orders
            </h2>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '48px'
            }}>
              <div style={{
                padding: '32px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{
                  fontSize: '3rem',
                  fontWeight: 900,
                  color: 'var(--gold-primary)',
                  marginBottom: '12px'
                }}>10%</div>
                <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '8px' }}>Bulk Discount</p>
                <p style={{ fontSize: '0.95rem', opacity: 0.85 }}>On orders of 5+ copies (shipped separately)</p>
              </div>
              
              <div style={{
                padding: '32px',
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'var(--gold-primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <Users size={28} color="white" />
                </div>
                <p style={{ fontSize: '1.1rem', fontWeight: 500, marginBottom: '8px' }}>Institutional Packages</p>
                <p style={{ fontSize: '0.95rem', opacity: 0.85 }}>Available for training programs</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/institutional" className="btn-gold" style={{ padding: '16px 32px', fontSize: '1rem' }}>
                View Institutional Packages <ArrowRight size={20} />
              </Link>
              <a href="mailto:info@vittafinance.ca" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '16px 32px',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '50px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                transition: 'transform 0.3s ease'
              }}>
                <Mail size={20} /> Email for Bulk Orders
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        input:focus,
        textarea:focus,
        select:focus {
          outline: none;
          border-color: var(--brand-primary) !important;
        }

        input::placeholder,
        textarea::placeholder {
          color: var(--text-muted);
        }

        .card-hover-effect:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 50px rgba(0,0,0,0.15);
        }
      `}</style>
    </div>
  );
};

export default Contact;
